import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  FormProvider,
  type FieldPath,
  type SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormDateInput } from '~/components/FormComponents/FormDateInput.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import {
  GuaranteesBankCommissionsRule,
  GuaranteesDeliveryMethod,
  GuaranteesEffectiveFromType,
  GuaranteesFormRule,
  GuaranteesSubordinationRule,
  type TGuaranteesFormValues,
} from '~/store/guarantees/types.ts';
import {
  GUARANTEE_FORM_BANK_COMMISSIONS_RULE_OPTIONS,
  GUARANTEE_FORM_DELIVERY_METHOD_OPTIONS,
  GUARANTEE_FORM_EFFECTIVE_FROM_TYPE_OPTIONS,
  GUARANTEE_FORM_REQUIRED_FIELDS,
  GUARANTEE_FORM_RULE_OPTIONS,
  GUARANTEE_FORM_SUBORDINATION_RULE_OPTIONS,
  GUARANTEE_FORM_TEXT,
} from '../../../rootSection/constants.ts';
import {
  GuaranteesFormFieldName,
  type IGuaranteeFormSectionProps,
} from '../../../rootSection/types.ts';
import {
  buildGuaranteesFormPayload,
  getGuaranteesFormInitialValues,
  guaranteesFormSectionResolver,
} from '../../../utils/sections/utils.ts';
import FlowSection from '../shared/FlowSection.tsx';
import { utils } from '../shared/utils.ts';
import { useGuaranteesRootApplicationSectionContext } from '../RootApplicationSectionContext.tsx';

const GuaranteeFormSection: React.FC<IGuaranteeFormSectionProps> = ({
  guaranteeForm,
  isLoading,
  isSaving,
  onSubmit,
}) => {
  const {
    isReadOnly,
    requireChangesToEnableActions,
    registerDependentSectionValidation,
  } = useGuaranteesRootApplicationSectionContext();
  const defaultValues = React.useMemo(
    () => getGuaranteesFormInitialValues(guaranteeForm),
    [guaranteeForm]
  );
  const formMethods = useForm<TGuaranteesFormValues>({
    defaultValues,
    resolver: guaranteesFormSectionResolver,
    mode: 'onSubmit',
  });
  const {
    control,
    getValues,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { isDirty },
  } = formMethods;
  const effectiveFromType = useWatch({
    control,
    name: GuaranteesFormFieldName.EffectiveFromType,
  });
  const deliveryMethod = useWatch({
    control,
    name: GuaranteesFormFieldName.DeliveryMethod,
  });
  const guaranteeFormRule = useWatch({
    control,
    name: GuaranteesFormFieldName.GuaranteeFormRule,
  });
  const bankCommissionsRule = useWatch({
    control,
    name: GuaranteesFormFieldName.BankCommissionsRule,
  });
  const subordinationRule = useWatch({
    control,
    name: GuaranteesFormFieldName.SubordinationRule,
  });
  const isDisabled = isSaving || isReadOnly;
  const shouldShowEffectiveFromDate =
    effectiveFromType?.value === GuaranteesEffectiveFromType.OTHER_DATE;
  const shouldShowDeliveryMethodOther =
    deliveryMethod?.value === GuaranteesDeliveryMethod.OTHER;
  const shouldShowGuaranteeFormRuleOther =
    guaranteeFormRule?.value === GuaranteesFormRule.OTHER;
  const shouldShowBankCommissionsRuleOther =
    bankCommissionsRule?.value === GuaranteesBankCommissionsRule.OTHER;
  const shouldShowSubordinationRuleOther =
    subordinationRule?.value === GuaranteesSubordinationRule.OTHER;
  const { isActionDisabled } = utils({
    defaultValues,
    isDirty,
    isDisabled,
    requireChangesToEnableActions,
    reset,
  });

  React.useEffect(() => {
    if (!effectiveFromType || effectiveFromType.value === GuaranteesEffectiveFromType.ISSUE_DATE) {
      setValue(GuaranteesFormFieldName.EffectiveFromDate, '', { shouldDirty: false });
    }
  }, [effectiveFromType, setValue]);

  React.useEffect(() => {
    if (deliveryMethod?.value !== GuaranteesDeliveryMethod.OTHER) {
      setValue(GuaranteesFormFieldName.DeliveryMethodOther, '', { shouldDirty: false });
    }
  }, [deliveryMethod, setValue]);

  React.useEffect(() => {
    if (guaranteeFormRule?.value !== GuaranteesFormRule.OTHER) {
      setValue(GuaranteesFormFieldName.GuaranteeFormRuleOther, '', { shouldDirty: false });
    }
  }, [guaranteeFormRule, setValue]);

  React.useEffect(() => {
    if (bankCommissionsRule?.value !== GuaranteesBankCommissionsRule.OTHER) {
      setValue(GuaranteesFormFieldName.BankCommissionsRuleOther, '', { shouldDirty: false });
    }
  }, [bankCommissionsRule, setValue]);

  React.useEffect(() => {
    if (subordinationRule?.value !== GuaranteesSubordinationRule.OTHER) {
      setValue(GuaranteesFormFieldName.SubordinationRuleOther, '', { shouldDirty: false });
    }
  }, [subordinationRule, setValue]);

  const touchAndValidateFields = React.useCallback(
    (fieldNames: readonly (keyof TGuaranteesFormValues)[]) => {
      fieldNames.forEach((fieldName) => {
        const fieldPath = fieldName as FieldPath<TGuaranteesFormValues>;

        setValue(fieldPath, getValues(fieldPath), {
          shouldDirty: false,
          shouldTouch: true,
          shouldValidate: false,
        });
      });

      void trigger(fieldNames as FieldPath<TGuaranteesFormValues>[]);
    },
    [getValues, setValue, trigger]
  );

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      touchAndValidateFields(GUARANTEE_FORM_REQUIRED_FIELDS);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [defaultValues, touchAndValidateFields]);

  React.useEffect(() => {
    if (shouldShowEffectiveFromDate) {
      touchAndValidateFields([GuaranteesFormFieldName.EffectiveFromDate]);
    }
  }, [shouldShowEffectiveFromDate, touchAndValidateFields]);

  React.useEffect(() => {
    if (shouldShowDeliveryMethodOther) {
      touchAndValidateFields([GuaranteesFormFieldName.DeliveryMethodOther]);
    }
  }, [shouldShowDeliveryMethodOther, touchAndValidateFields]);

  React.useEffect(() => {
    if (shouldShowGuaranteeFormRuleOther) {
      touchAndValidateFields([GuaranteesFormFieldName.GuaranteeFormRuleOther]);
    }
  }, [shouldShowGuaranteeFormRuleOther, touchAndValidateFields]);

  React.useEffect(() => {
    if (shouldShowBankCommissionsRuleOther) {
      touchAndValidateFields([GuaranteesFormFieldName.BankCommissionsRuleOther]);
    }
  }, [shouldShowBankCommissionsRuleOther, touchAndValidateFields]);

  React.useEffect(() => {
    if (shouldShowSubordinationRuleOther) {
      touchAndValidateFields([GuaranteesFormFieldName.SubordinationRuleOther]);
    }
  }, [shouldShowSubordinationRuleOther, touchAndValidateFields]);

  React.useEffect(
    () => registerDependentSectionValidation('guaranteeForm', () => trigger(undefined, { shouldFocus: true })),
    [registerDependentSectionValidation, trigger]
  );

  const handleGuaranteeFormSubmit: SubmitHandler<TGuaranteesFormValues> = async (values) => {
    const isSaved = await onSubmit(buildGuaranteesFormPayload(values));

    if (isSaved) {
      reset(values);
    }
  };

  return (
    <FlowSection
      step={GUARANTEE_FORM_TEXT.sectionStepLabel}
      title={GUARANTEE_FORM_TEXT.sectionTitle}
      description={GUARANTEE_FORM_TEXT.sectionDescription}
    >
      {isLoading ? (
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          <CircularProgress size={24} />
          <Typography sx={guaranteesClientPageStyles.flowLockHint}>
            {GUARANTEE_FORM_TEXT.loadingText}
          </Typography>
        </Box>
      ) : (
        <FormProvider {...formMethods}>
          <Box
            component="form"
            onSubmit={(event) => {
              void handleSubmit(handleGuaranteeFormSubmit)(event);
            }}
          >
            <Box sx={guaranteesClientPageStyles.flowFieldsGrid}>
              {/* <FormInput
                name={GuaranteesFormFieldName.IssuePlace}
                label={GUARANTEE_FORM_TEXT.issuePlaceLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              /> */}
              {/* <FormDateInput
                name={GuaranteesFormFieldName.IssueDate}
                label={GUARANTEE_FORM_TEXT.issueDateLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              /> */}
              {/* <FormInput
                name={GuaranteesFormFieldName.DocumentsLanguage}
                label={GUARANTEE_FORM_TEXT.documentsLanguageLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              /> */}
              <FormInput
                name={GuaranteesFormFieldName.DemandPresentationForm}
                label={GUARANTEE_FORM_TEXT.demandPresentationFormLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              {/* <FormInput
                name={GuaranteesFormFieldName.DemandPresentationPlace}
                label={GUARANTEE_FORM_TEXT.demandPresentationPlaceLabel}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              /> */}
              <FormAutocomplete
                name={GuaranteesFormFieldName.EffectiveFromType}
                label={GUARANTEE_FORM_TEXT.effectiveFromTypeLabel}
                placeholder={GUARANTEE_FORM_TEXT.effectiveFromTypePlaceholder}
                options={GUARANTEE_FORM_EFFECTIVE_FROM_TYPE_OPTIONS}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              {shouldShowEffectiveFromDate && (
                <FormDateInput
                  name={GuaranteesFormFieldName.EffectiveFromDate}
                  label={GUARANTEE_FORM_TEXT.effectiveFromDateLabel}
                  disabled={isDisabled}
                  sx={guaranteesClientPageStyles.flowField}
                />
              )}
              <FormInput
                name={GuaranteesFormFieldName.SupportingDocuments}
                label={GUARANTEE_FORM_TEXT.supportingDocumentsLabel}
                disabled={isDisabled}
                multiline
                minRows={3}
                sx={guaranteesClientPageStyles.flowFullWidthField}
              />
              <FormAutocomplete
                name={GuaranteesFormFieldName.SubordinationRule}
                label={GUARANTEE_FORM_TEXT.subordinationRuleLabel}
                placeholder={GUARANTEE_FORM_TEXT.subordinationRulePlaceholder}
                options={GUARANTEE_FORM_SUBORDINATION_RULE_OPTIONS}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              {shouldShowSubordinationRuleOther && (
                <FormInput
                  name={GuaranteesFormFieldName.SubordinationRuleOther}
                  label={GUARANTEE_FORM_TEXT.subordinationRuleOtherLabel}
                  disabled={isDisabled}
                  sx={guaranteesClientPageStyles.flowField}
                />
              )}
              <FormAutocomplete
                name={GuaranteesFormFieldName.DeliveryMethod}
                label={GUARANTEE_FORM_TEXT.deliveryMethodLabel}
                placeholder={GUARANTEE_FORM_TEXT.deliveryMethodPlaceholder}
                options={GUARANTEE_FORM_DELIVERY_METHOD_OPTIONS}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              {shouldShowDeliveryMethodOther && (
                <FormInput
                  name={GuaranteesFormFieldName.DeliveryMethodOther}
                  label={GUARANTEE_FORM_TEXT.deliveryMethodOtherLabel}
                  disabled={isDisabled}
                  sx={guaranteesClientPageStyles.flowField}
                />
              )}
              <FormAutocomplete
                name={GuaranteesFormFieldName.GuaranteeFormRule}
                label={GUARANTEE_FORM_TEXT.guaranteeFormRuleLabel}
                placeholder={GUARANTEE_FORM_TEXT.guaranteeFormRulePlaceholder}
                options={GUARANTEE_FORM_RULE_OPTIONS}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              {shouldShowGuaranteeFormRuleOther && (
                <FormInput
                  name={GuaranteesFormFieldName.GuaranteeFormRuleOther}
                  label={GUARANTEE_FORM_TEXT.guaranteeFormRuleOtherLabel}
                  disabled={isDisabled}
                  sx={guaranteesClientPageStyles.flowField}
                />
              )}
              <FormAutocomplete
                name={GuaranteesFormFieldName.BankCommissionsRule}
                label={GUARANTEE_FORM_TEXT.bankCommissionsRuleLabel}
                placeholder={GUARANTEE_FORM_TEXT.bankCommissionsRulePlaceholder}
                options={GUARANTEE_FORM_BANK_COMMISSIONS_RULE_OPTIONS}
                disabled={isDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              {shouldShowBankCommissionsRuleOther && (
                <FormInput
                  name={GuaranteesFormFieldName.BankCommissionsRuleOther}
                  label={GUARANTEE_FORM_TEXT.bankCommissionsRuleOtherLabel}
                  disabled={isDisabled}
                  sx={guaranteesClientPageStyles.flowField}
                />
              )}
            </Box>

            <Box sx={guaranteesClientPageStyles.flowActions}>
              <Button
                type="submit"
                variant="contained"
                disabled={isActionDisabled}
                startIcon={isSaving ? <ButtonLoadingIcon /> : undefined}
                sx={dataFormStyles.documentPrimaryAction}
              >
                {GUARANTEE_FORM_TEXT.saveButton}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      )}
    </FlowSection>
  );
};

export default GuaranteeFormSection;
